/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert, Input, InputNumber, Select, message } from 'antd'
import BaseModal from 'src/components/ts-components/BaseModal'
import { useEffect, useState } from 'react'
import type {
  BalanceLineKind,
  BalanceSlot,
  OfflinePaymentBody,
  OfflinePaymentPreview,
} from '@sportninja/common/services/teamBalance'
import { previewOfflinePayment } from '@sportninja/common/services/teamBalance'
import colors from '@sportninja/common/constants/appColors'
import Price from 'src/components/ts-components/Price'
import { dollarsToMinor } from '../utils/balanceMath'
import { guardMessage } from '../utils/balanceError'
import {
  balanceLabel,
  balanceText,
  billingTypeLabel,
} from '../utils/balanceI18n'
import { offlinePaymentPrefill } from '../utils/balanceTax'

const SOURCES = ['cash', 'e-transfer', 'dash', 'pos', 'other'] as const

const FULL_WIDTH = { width: '100%' } as const

type OfflinePaymentModalProps = {
  open: boolean
  teamId: string
  /** D40 — the sheet's roster; preview + record are roster-scoped. */
  rosterId: string
  slot: BalanceSlot | null
  currencyCode: string
  submitting: boolean
  onConfirm: (body: OfflinePaymentBody) => Promise<unknown>
  onClose: () => void
  /** Sep 23 — `official` when the cash is recorded on a team official's line. */
  expectedKind?: BalanceLineKind
  /**
   * Sep 23 (D8) — the recorder pays this line: a league admin, not they, verifies it,
   * so the modal says so before the cash is recorded.
   */
  ownLine?: boolean
}

/**
 * Records an offline payment (cash / e-transfer / dash / POS) against a slot's player. The amount
 * is applied oldest-first by the SERVER; a live preview (C15) shows exactly what will settle or
 * split — the client never computes split math. A fresh idempotency key per open makes a
 * dropped-but-committed submit safe. Over-payment is allowed (shown as a surplus credit).
 */
const OfflinePaymentModal = ({
  open,
  teamId,
  rosterId,
  slot,
  currencyCode,
  submitting,
  onConfirm,
  onClose,
  expectedKind,
  ownLine = false,
}: OfflinePaymentModalProps) => {
  'use memo'
  const [amount, setAmount] = useState<number | null>(null)
  const [source, setSource] = useState<string>('cash')
  // A1 — the rep's reference for this payment, captured AT RECORD TIME because
  // that is the only moment they are holding the cheque / terminal slip.
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [preview, setPreview] = useState<OfflinePaymentPreview | null>(null)

  useEffect(() => {
    if (open) {
      setIdempotencyKey(
        `op-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
      )
      // Tax on top: start from what the player still owes INCLUDING tax, so
      // recording "the rest" never leaves the tax behind as a balance of its own.
      setAmount(offlinePaymentPrefill(slot))
      setSource('cash')
      setReference('')
      setNote('')
      setPreview(null)
    }
    // Once per OPEN, deliberately: `slot` is re-resolved from every refetch, and a
    // background refresh must never overwrite an amount the rep has already typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const amountCents = Math.round((amount ?? 0) * 100)

  useEffect(() => {
    if (!open || !slot || amountCents <= 0) {
      setPreview(null)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      previewOfflinePayment(teamId, rosterId, slot.id, {
        amount_cents: amountCents,
        source,
        idempotency_key: idempotencyKey,
        ...(expectedKind ? { expected_kind: expectedKind } : {}),
      })
        .then((result) => {
          if (!cancelled) {
            setPreview(result)
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPreview(null)
          }
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [
    open,
    slot,
    amountCents,
    teamId,
    rosterId,
    source,
    idempotencyKey,
    expectedKind,
  ])

  const submit = () => {
    if (amountCents <= 0) {
      message.error(balanceText('common:balanceOfflineAmountRequired'))
      return
    }
    // D39: a recording must name the exact plan the admin was shown. Without a
    // loaded preview there is no fingerprint to send and the server would refuse
    // it, so don't attempt the call at all.
    if (!preview?.settlement_fingerprint) {
      message.error(balanceText('common:balanceOfflinePreviewRequired'))
      return
    }
    onConfirm({
      amount_cents: amountCents,
      source,
      // A1: the reference joins the request fingerprint server-side, so it must
      // normalise the same way on every attempt — blank is null, never ''.
      reference: reference.trim() ? reference.trim() : null,
      note: note.trim() ? note.trim() : null,
      idempotency_key: idempotencyKey,
      settlement_fingerprint: preview.settlement_fingerprint,
      ...(expectedKind ? { expected_kind: expectedKind } : {}),
    })
      .then(() => onClose())
      .catch((error) => message.error(guardMessage(error)))
  }

  return (
    <BaseModal
      isOpen={open}
      title={balanceLabel('common:balanceRecordOffline')}
      okText={balanceLabel('common:balanceOfflineRecordAction')}
      cancelText={balanceLabel('common:balanceCommsCancel')}
      isLoading={submitting}
      disableOk={amountCents <= 0 || !preview?.settlement_fingerprint}
      onClose={onClose}
      onOk={submit}
      desktopWidth='560px'
      destroyOnClose
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        {ownLine ? (
          <Alert
            type='info'
            showIcon
            data-testid='offline-own-line-note'
            message={balanceText('common:balanceOfficialOwnLineRecordNote')}
          />
        ) : null}
        <label
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <span>{balanceLabel('common:balanceOfflineAmount')}</span>
          <InputNumber
            data-testid='offline-amount'
            value={amount ?? undefined}
            min={0.01}
            step={1}
            precision={2}
            prefix='$'
            style={FULL_WIDTH}
            onChange={(value) =>
              setAmount(typeof value === 'number' ? value : null)
            }
          />
        </label>

        <label
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <span>{balanceLabel('common:balanceOfflineSource')}</span>
          <Select
            data-testid='offline-source'
            value={source}
            onChange={setSource}
            options={SOURCES.map((value) => ({
              value,
              label: balanceLabel(`common:balanceOfflineSource_${value}`),
            }))}
          />
        </label>

        {/* Above the note on purpose: the reference is transaction FACT (and
            prints on the player's receipt), the note is commentary. */}
        <label
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <span>{balanceLabel('common:balanceOfflineReference')}</span>
          <Input
            data-testid='offline-reference'
            value={reference}
            maxLength={100}
            placeholder={balanceText(
              'common:balanceOfflineReferencePlaceholder'
            )}
            onChange={(event) => setReference(event.target.value)}
          />
        </label>

        <label
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <span>{balanceLabel('common:balanceOfflineNote')}</span>
          <Input.TextArea
            data-testid='offline-note'
            value={note}
            maxLength={500}
            rows={2}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {preview ? (
          <div
            data-testid='offline-preview'
            css={css`
              border-top: 1px solid ${colors.SECONDARY_300};
              padding-top: 8px;
              display: flex;
              flex-direction: column;
              gap: 4px;
              font-size: 13px;
            `}
          >
            <span
              css={css`
                color: ${colors.NEUTRAL_100};
              `}
            >
              {balanceText('common:balanceOfflinePreviewHeading')}
            </span>
            {preview.applications.map((application) => (
              <div
                key={application.bill_uid}
                css={css`
                  display: flex;
                  justify-content: space-between;
                `}
              >
                <span>
                  {application.action === 'settle'
                    ? balanceLabel('common:balanceOfflineSettles', {
                        type: billingTypeLabel(application.bill_type),
                      })
                    : balanceLabel('common:balanceOfflineSplits', {
                        type: billingTypeLabel(application.bill_type),
                      })}
                </span>
                <Price
                  amount={dollarsToMinor(application.applied_total)}
                  currency={currencyCode}
                />
              </div>
            ))}
            {preview.surplus > 0 ? (
              <div
                data-testid='offline-preview-surplus'
                css={css`
                  display: flex;
                  justify-content: space-between;
                  color: ${colors.NEUTRAL_100};
                `}
              >
                <span>{balanceLabel('common:balanceOfflineSurplus')}</span>
                <Price
                  amount={dollarsToMinor(preview.surplus)}
                  currency={currencyCode}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </BaseModal>
  )
}

export default OfflinePaymentModal
